import { put, takeLatest, fork, take, call, select } from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'
import ServiceService from '../../../services/Service'
import ListMetaData from '../../../modules/utilities/list/ListMetaData'
import FilterExpression from '../../../modules/utilities/list/FilterExpression'
import FilterParameter from '../../../modules/utilities/list/FilterParameter'
import ServiceDataStoreSlice from '../../slices/data/ServiceDataStoreSlice'
import ErrorActions from '../../actions/Error'
import OLError from '../../../modules/errorHandling/models/OLError'
import ServiceManagementExperienceActions from '../../actions/experiences/ServiceManagement'
import SERVICE_MANAGEMENT_EXPERIENCE_TYPES from '../../types/experiences/ServiceManagement'
import LayoutActions from '../../actions/Layout'
import ServiceManagementServiceListSlice from '../../slices/experiences/ServiceManagementServiceListSlice'
import MessageActions from '../../actions/Messages'
import { AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT, DISPLAYED_MESSAGE_TYPES } from '../../../constants/general'
import MissionService from '../../../services/Mission'
import ServiceManagementServiceListExportSlice from '../../slices/experiences/ServiceManagementServiceListExportSlice'
import FormSocket from '../../../services/socket/FormSocket'
import { stripEmptyGridFilters } from '../../../modules/dataGrid/utilities/gridFilterUtils'
import {
  normalizeFormFilledSocketPayload,
  canViewerOpenPriseDeServiceDialog,
  shouldRefreshActiveServicesList,
  resolveFieldOperationServiceId
} from '../../../modules/forms/formFilledSocketNormalization'

function * fetchServices (action) {
  const sliceActions = action.type === ServiceManagementServiceListExportSlice.types.FETCH
    ? ServiceManagementExperienceActions.exportList
    : ServiceManagementExperienceActions.list
  try {
    const { data: { limit, page, sort, filters = [], filtersLinkingType } } = action
    yield put(sliceActions.setIsLoading(true))

    const filterArgs = stripEmptyGridFilters(filters)
    const filter = filterArgs.length > 0 ? new FilterExpression(filtersLinkingType, ...filterArgs) : null

    const result = yield ServiceService.FindMany(
      new ListMetaData(limit, page, sort, filter)
    )
    yield put(
      ServiceDataStoreSlice.actions.fetchResult(
        result,
        function * (storeValues) {
          yield put(
            sliceActions.setData(storeValues.result, storeValues.totalNrOfItems, false)
          )
        }
      )
    )
  } catch (e) {
    yield put(sliceActions.setIsLoading(false))
    yield put(ErrorActions.trigger(
      new OLError('[ServiceManagementExperience->fetchServices]: Failed to fetch services', 'Failed to fetch services', e),
      true,
      true,
      true,
      false
    ))
  }
}

function * editMission (action) {
  const fetchId = 'ServiceManagementExperienceSaga.editMission'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { missionItem, onSuccess } } = action

    const result = yield MissionService.Update(missionItem.id, missionItem.toNormalizedRESTModel())
    if (result.status === 200) {
      // display success message
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['EDIT_MISSION_SUCCESS', 'Mission successfully updated'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
      onSuccess && onSuccess()
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[ServiceManagementExperience->editService]: Failed to update mission', 'Failed to update mission', e),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * refreshActiveServicesList () {
  yield put(
    ServiceManagementExperienceActions.list.fetch(
      ListMetaData.NO_LIMIT,
      0,
      [],
      [
        new FilterParameter(
          FilterParameter.FILTER_TYPES.EQUALS,
          'activeStatus',
          'active'
        )
      ],
      FilterExpression.EXPRESSION_TYPE.AND
    )
  )
}

function * handleFormFilledRealtime (filled, eventType) {
  const serviceId = resolveFieldOperationServiceId(filled)

  if (shouldRefreshActiveServicesList(filled) || eventType === 'updated') {
    yield call(refreshActiveServicesList)
  }

  if (!serviceId) return

  const profile = yield select(state => state.experiences.auth.profile)
  if (canViewerOpenPriseDeServiceDialog(profile, filled)) {
    yield put(ServiceDataStoreSlice.actions.fetch([serviceId]))
    yield put(ServiceManagementExperienceActions.addPriseDeService(serviceId))
    return
  }

  if (eventType === 'created' || eventType === 'updated') {
    yield put(ServiceDataStoreSlice.actions.fetch([serviceId]))
  }
}

function * ServiceManagementExperienceSaga () {
  yield fork(function * watchFormFilledEvents () {
    const channel = eventChannel(emit => {
      FormSocket.onFormFilledEvents((payload, eventType) => emit({ payload, eventType }))
      return () => {}
    })

    while (true) {
      try {
        const { payload, eventType } = yield take(channel)
        const filled = normalizeFormFilledSocketPayload(payload)
        if (!filled?.form_filled_id) continue
        yield call(handleFormFilledRealtime, filled, eventType)
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[ServiceManagement] form_filled realtime handler failed:', e?.message || e)
        }
      }
    }
  })

  yield takeLatest(ServiceManagementServiceListSlice.types.FETCH, fetchServices)
  yield takeLatest(ServiceManagementServiceListExportSlice.types.FETCH, fetchServices)
  yield takeLatest(SERVICE_MANAGEMENT_EXPERIENCE_TYPES.EDIT_MISSION, editMission)
}

export default ServiceManagementExperienceSaga
